import { useEffect, useMemo, useState } from "react";
import TableV2 from "../TableV2/tableV2";
import { DATE_FORMATS, formatDate } from "../../Util/dateUtils";
import * as XLSX from 'xlsx';
import { useSelector } from "react-redux";
import { companyConfigurationsSelector } from "../../Reducers/companyConfigurtionsReducer";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";

const columnConfig = [
    {
        id: "companyName",
        label: "Business Name",
        data: x => x.companyName,
    },
    {
        id: "place_date",
        label: "Date",
        data: x => formatDate(x.placeTime, DATE_FORMATS.MMM_DDc_YYYY),
    },
    {
        id: "day_of_week",
        label: "Day of Week",
        data: x => new Date(x.placeTime).toLocaleDateString('en-US', { weekday: 'long' }),
        show: false,
    },
    {
        id: "order_no",
        label: "Order No.",
        data: x => x.orderNo,
    },
    {
        id: "pickup_from",
        label: "Pick-up From",
        data: x => x.pickUpFrom,
        tableClass: "wide-column"
    },
    {
        id: "pickup_address",
        label: "Pick-up Address",
        data: x => x.pickUpAddress,
        show: false,
    },
    {
        id: "customer_name",
        label: "Deliver To",
        data: x => x.customerName,
        tableClass: "wide-column"
    },
    {
        id: "customer_address",
        label: "Delivery Address",
        data: x => x.customerAddress,
        show: false,
    },
    {
        id: "driver",
        label: "Driver",
        data: x => x.driver || "--",
    },
    {
        id: "driver_type",
        label: "Driver Type",
        data: x => {
            if (!x.driver) {
                return "--";
            }

            if (x.thirdPartyAssigned) {
                return "Third Party";
            }
            else {
                return "In-house";
            }
        },
        show: false,
    },
    {
        id: "total",
        label: "Total",
        data: x => x.currency + Number(x.total).toFixed(2),
    },
    {
        id: "payment_method",
        label: "Payment Method",
        data: x => x.paymentMethod || "N/A",
    },
    {
        id: "delivery_fee",
        label: "Delivery Fee",
        data: x => x.currency + Number(x.deliveryFee).toFixed(2),
    },
    {
        id: "driver_payment",
        label: "Base Driver Pay",
        data: x => x.currency + Number(x.driverPayment).toFixed(2),
    },
    {
        id: "cash_tip",
        label: "Online Tips",
        data: x => x.currency + Number(x.cashTip).toFixed(2),
    },
    {
        id: "delivery_distance_mile",
        label: "Distance (mile)",
        data: x => (Math.round(x.deliveryDistance * 100) / 100).toFixed(2),
    },
    {
        id: "delivery_distance_km",
        label: "Distance (km)",
        data: x => (Math.round(x.deliveryDistance * 1.60934 * 100)/100).toFixed(2),
    },
    {
        id: "feedback",
        label: "Feedback",
        data: x => {
            if (x.feedback == null || x.feedback === 0) return 'N/A';
            if (x.feedback > 0 && x.feedback <= 5)
                return <div className="feedback-with-star">
                            <span className="feedback-number">{x.feedback}</span>
                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-star-fill" viewBox="0 0 16 16">
                                  <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                                </svg>
                            </span>
                       </div>
        }, 
        textData: x => {
            if (x.feedback == null || x.feedback === 0) return 'N/A';
            else return x.feedback;
        }
    },
    {
        id: "place_time",
        label: "Placement Time",
        data: x => formatDate(x.placeTime, DATE_FORMATS.HHcMM_AP),
        show: false,
    },
    {
        id: "expected_pickup_time",
        label: "Requested Pickup Time",
        data: x => x.requestedPickup ? formatDate(x.requestedPickup, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "expected_delivery_date",
        label: "Requested Delivery Date",
        data: x => formatDate(x.expectedDeliveryDate, DATE_FORMATS.MMM_DDc_YYYY),
        show: false,
    },
    {
        id: "expected_delivery_time",
        label: "Requested Delivery Time",
        data: x => x.requestedDelivery ? formatDate(x.requestedDelivery, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "assign_time",
        label: "Order Assignment Time",
        data: x => x.assignTime ? formatDate(x.assignTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "accept_time",
        label: "Order Accepted Time",
        data: x => x.acceptTime ? formatDate(x.acceptTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "ready_to_pickup_time",
        label: "Ready to Pickup Time",
        data: x => x.readyToPickupTime ? formatDate(x.readyToPickupTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "pickup_time",
        label: "Pick up Time",
        data: x => x.pickupTime ? formatDate(x.pickupTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "out_the_door",
        label: "Out the Door",
        data: x => x.outTheDoor ? formatDate(x.outTheDoor, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "on_the_way_time",
        label: "On The Way Time",
        data: x => x.onTheWayTime ? formatDate(x.onTheWayTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "at_the_customer",
        label: "At the Customer",
        data: x => x.atTheCustomer ? formatDate(x.atTheCustomer, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "completion_date",
        label: "Completion Date",
        data: x => x.completionTime ? formatDate(x.completionTime, DATE_FORMATS.MMM_DDc_YYYY) : "",
        show: false,
    },
    {
        id: "completion_time",
        label: "Completion Time",
        data: x => x.completionTime ? formatDate(x.completionTime, DATE_FORMATS.HHcMM_AP) : "",
        show: false,
    },
    {
        id: "assigner_name",
        label: "Assigned by",
        data: x => x.assignerName || "N/A",
        show: false,
    },
    {
        id: "delivery_instruction",
        label: "Delivery Instruction",
        data: x => x.deliveryInstruction || "N/A",
        show: false,
    },
    {
        id: "delivery_note",
        label: "Delivery Note",
        data: x => x.deliveryNote || "N/A",
        show: false,
    },
    {
        id: "signature_path",
        label: "Proof of Delivery",
        data: x => {
            if (!x.signaturePath) return "N/A";
            const signaturePath = x.signaturePath.split(":_SIG_:")[0].split(':_:_:')[0];
            return signaturePath;
        },
        tableClass: "wide-column"
    },
    {
        id: "placement_to_out_the_door",
        label: "Placement to Out the Door (in mins)",
        data: x => x.placementToOutTheDoor,
        show: false,
    },   
    {
        id: "out_the_door_to_at_the_customer",
        label: "Out the Door to At the Customer (in mins)",
        data: x => x.outTheDoorToAtTheCustomer,
        show: false,
    },
    {
        id: "placement_to_at_the_customer",
        label: "Placement to At the Customer (in mins)",
        data: x => x.placementToAtTheCustomer,
        show: false,
    },
    {
        id: "status",
        label: "Status",
        data: x => x.failedOrder ? "Failed" : "Delivered",
    },
    {
        id: "on_time_delivery",
        label: "On Time Delivery",
        data: x => {
            const actualTime = x.atTheCustomer ?? x.completionTime;

            if (!actualTime || !x.requestedDelivery) return "No";

            return new Date(actualTime) <= new Date(x.requestedDelivery) ? "Yes" : "No";
        },
        show: false,
    },
    {
        id: "delivery_within_40_min",
        label: "Delivery Within 40 Minutes",
        data: x => {
            const actualTime = x.atTheCustomer ?? x.completionTime;
            if (!actualTime || !x.placeTime) return "No";
            return (new Date(actualTime) - new Date(x.placeTime)) <= 40 * 60 * 1000 ? "Yes" : "No";
        },
        show: false,
    },
    {
        id: "pickup_lateness",
        label: "Pickup Lateness (mins)",
        data: x => {
            const expectedPickupTime = x.requestedPickup ? new Date(x.requestedPickup) : null;

            if (!expectedPickupTime || !x.pickupTime) return "N/A";

            return Math.max(0, Math.round((new Date(x.pickupTime) - expectedPickupTime) / (60 * 1000)));
        },
        show: false
    },
    {
        id: "delivery_lateness",
        label: "Delivery Lateness (mins)",
        data: x => {
            const actualTime = x.atTheCustomer ?? x.completionTime;
            const expectedDeliveryTime = x.requestedDelivery ? new Date(x.requestedDelivery) : null;

            if (!expectedDeliveryTime || !actualTime) return "N/A";

            return Math.max(0, Math.round((new Date(actualTime) - expectedDeliveryTime) / (60 * 1000)));
        },
        show: false
    }
]

const tableConfig = {
    columns: columnConfig,
    pagination: true,
    pageSize: 50,
    scrollable: true
}

const ExtendedReportTable = ({ data, selectedCompanies, searchTerm, setDownloadHandler }) => {
  const configurations = useSelector(companyConfigurationsSelector);
  const { data: partnerAccounts } = useFetchCompanyInfosQuery();

  const orders = useMemo(() => {
    if (data === null) return [];
    var orders = data.getOrdersForCompany(selectedCompanies);
    orders.forEach(o => {
        o["currency"] = configurations === null ? "" : configurations?.getCurrency(o.companyId) ?? "";
        o["companyName"] = partnerAccounts === null ? "" : partnerAccounts?.getAccountDetailsByCompanyId(o.companyId)?.companyName ?? "";
    })
    return orders;
  }, [data, selectedCompanies, configurations, partnerAccounts]);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredOrders = useMemo(() => {
    if (!debouncedSearchTerm) return orders;
    
    return orders.filter(row => {
      return tableConfig.columns.some(column => {
        if (!(column.show ?? true)) return false;
        const cellValue = column.data(row);
        const stringValue = typeof cellValue === 'number' ? cellValue.toString() : cellValue;
        return typeof stringValue === 'string' && 
               stringValue.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      });
    });
  }, [orders, debouncedSearchTerm]);

  useEffect(() => {
    setDownloadHandler(() => () => {
      const workbook = XLSX.utils.book_new();
      const headers = columnConfig.map(col => col.label);
      const data = filteredOrders.map(order => {
        return columnConfig.map(col => {
          return col.textData ? col.textData(order) : col.data(order);
        });
      });

      data.unshift(headers);

      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

      XLSX.writeFile(workbook, 'Extended Order Report.xlsx');
    });
  }, [setDownloadHandler, filteredOrders]);

  return <div style={{width: "100%"}}>
    <TableV2 data={filteredOrders} config={tableConfig} />
  </div>;
};

export default ExtendedReportTable;
